import { ActionIcon, Menu, RingProgress, Text, Box, Tooltip } from '@mantine/core';
import { IconCheck, IconAlertTriangle, IconLoader2, IconHistory } from '@tabler/icons-react';
import { useMemo } from 'react';
import {
  messageQueue,
  selectCounts,
  useMessageQueue,
  type QueueEntry,
} from '../state/messageQueue';

interface Props {
  conversationId: number | undefined;
  // Scroll a message into view when the user clicks a queue entry.
  onScrollToMessage?: (entry: QueueEntry) => boolean;
}

const SIZE = 48;

export function MessageQueueBadge({ conversationId, onScrollToMessage }: Props) {
  const entries = useMessageQueue(conversationId);
  const counts = useMemo(() => selectCounts(entries), [entries]);
  // Dropdown shows entries newest-first. Everything stays in the list (per the
  // "don't remove from queue" rule) so the user can scroll back to old ones.
  const visible = useMemo(() => entries.slice().reverse(), [entries]);

  if (!conversationId) return null;

  const hasEntries = counts.allTotal > 0;
  const ringColor = counts.failed > 0 ? 'red' : counts.inFlight > 0 ? 'blue' : 'green';
  const pct = counts.total > 0 ? Math.round((counts.done / counts.total) * 100) : 100;

  return (
    <Menu
      position="top-start"
      shadow="md"
      width={360}
      onOpen={() => conversationId && messageQueue.markAllRead(conversationId)}
    >
      <Menu.Target>
        <Tooltip
          label={
            hasEntries
              ? `${counts.done}/${counts.total} done${counts.inFlight > 0 ? ` · ${counts.inFlight} in flight` : ''}${counts.failed > 0 ? ` · ${counts.failed} failed` : ''}`
              : 'Message history'
          }
          position="top"
          withArrow
        >
          <ActionIcon
            size={SIZE}
            radius="xl"
            variant="subtle"
            aria-label="Message queue"
            style={{ position: 'relative', flexShrink: 0 }}
          >
            {hasEntries ? (
              <>
                <RingProgress
                  size={SIZE}
                  thickness={4}
                  roundCaps
                  sections={[{ value: pct, color: ringColor }]}
                  rootColor="#2A2A2E"
                  style={{
                    animation: counts.inFlight > 0 ? 'msgq-spin 1.4s linear infinite' : undefined,
                  }}
                />
                <Text
                  size="13px"
                  fw={700}
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    fontVariantNumeric: 'tabular-nums',
                    pointerEvents: 'none',
                  }}
                >
                  {counts.done}/{counts.total}
                </Text>
              </>
            ) : (
              <IconHistory size={20} />
            )}
            <style>{`@keyframes msgq-spin { to { transform: rotate(360deg); } }`}</style>
          </ActionIcon>
        </Tooltip>
      </Menu.Target>

      <Menu.Dropdown>
        <Menu.Label>
          {hasEntries
            ? `Queue · ${counts.done}/${counts.total} done${counts.allTotal !== counts.total ? ` · ${counts.allTotal - counts.total} read` : ''}`
            : 'Message history'}
        </Menu.Label>
        {visible.length === 0 && (
          <Box px="sm" py="xs">
            <Text size="xs" c="dimmed">No messages yet.</Text>
          </Box>
        )}
        {visible.map((entry) => (
          <Menu.Item
            key={entry.id}
            onClick={() => onScrollToMessage?.(entry)}
            leftSection={statusIcon(entry.status)}
            style={{ opacity: entry.read ? 0.65 : 1 }}
          >
            <Box style={{ minWidth: 0 }}>
              <Text size="xs" fw={500} lineClamp={1}>
                {entry.content || '(no text)'}
              </Text>
              <Text size="10px" c="dimmed">
                {statusLabel(entry.status)} · {relativeTime(entry.createdAt)}
                {entry.read ? ' · read' : ''}
              </Text>
            </Box>
          </Menu.Item>
        ))}
      </Menu.Dropdown>
    </Menu>
  );
}

function statusIcon(status: QueueEntry['status']) {
  if (status === 'done') return <IconCheck size={14} color="#22C55E" />;
  if (status === 'failed') return <IconAlertTriangle size={14} color="#EF4444" />;
  return (
    <Box style={{ animation: 'msgq-spin 1s linear infinite', display: 'inline-flex' }}>
      <IconLoader2 size={14} color="#60A5FA" />
    </Box>
  );
}

function statusLabel(status: QueueEntry['status']) {
  if (status === 'done') return 'Done';
  if (status === 'failed') return 'Failed';
  return 'In flight';
}

function relativeTime(then: number): string {
  const sec = Math.max(0, Math.round((Date.now() - then) / 1000));
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  return `${hr}h ago`;
}
